<template>
  <v-card>
    <v-list>
      <v-list-subheader>Family</v-list-subheader>
      <v-list-item
        v-for="member in members"
        :key="member.uid"
        :title="member.displayName || member.email"
        :subtitle="member.email"
      >
        <template v-slot:prepend>
          <v-avatar>
            <v-img v-if="member.photoUrl" :src="member.photoUrl" alt="" />
            <v-icon v-else icon="mdi-account" />
          </v-avatar>
        </template>
        <template v-slot:append>
          <v-btn
            icon="mdi-close"
            variant="text"
            :aria-label="`Remove ${member.displayName || member.email}`"
            @click="confirmRemove(member)"
          />
        </template>
      </v-list-item>
      <v-list-item v-if="members.length === 0" subtitle="No family members yet">
        <template v-slot:prepend>
          <v-avatar>
            <v-icon icon="mdi-account-group" />
          </v-avatar>
        </template>
      </v-list-item>
    </v-list>
    <v-card-actions v-if="!inviteUrl">
      <v-btn
        prepend-icon="mdi-link-plus"
        variant="tonal"
        @click="generateInvite"
        :loading="generatingInvite"
      >
        <template #loader>
          <v-progress-circular
            indeterminate
            width="2"
            aria-label="Generating invite link"
          />
        </template>
        Invite family member
      </v-btn>
    </v-card-actions>
    <v-card-text v-if="inviteUrl">
      <v-text-field
        variant="outlined"
        :model-value="inviteUrl"
        :readonly="true"
        prepend-icon="mdi-link"
        append-icon="mdi-content-copy"
        label="Share this link"
        @click:append="copyToClipboard"
      />
    </v-card-text>
    <v-dialog
      v-model="removeDialog"
      max-width="400"
      aria-label="Remove family member"
    >
      <v-card>
        <v-card-title>Remove family member</v-card-title>
        <v-card-text>
          Are you sure you want to remove
          <strong>{{
            memberToRemove?.displayName || memberToRemove?.email
          }}</strong>
          from your family? You will no longer see each other's recipes.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="removeDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="removeMember">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="copiedSnackbar"> Copied to clipboard! </v-snackbar>
  </v-card>
</template>

<script lang="ts">
  import { defineComponent, type PropType, ref } from 'vue';
  import type { Profile } from '@/service/profile/types';

  export default defineComponent({
    name: 'FamilyMembersCard',
    props: {
      members: {
        type: Array as PropType<Profile[]>,
        required: true,
      },
    },
    emits: ['generate-invite', 'remove-member'],
    setup(_, { emit }) {
      const inviteUrl = ref<string | undefined>(undefined);
      const generatingInvite = ref(false);
      const copiedSnackbar = ref(false);
      const removeDialog = ref(false);
      const memberToRemove = ref<Profile | undefined>(undefined);

      return {
        inviteUrl,
        generatingInvite,
        copiedSnackbar,
        removeDialog,
        memberToRemove,
        async generateInvite() {
          generatingInvite.value = true;
          emit('generate-invite', (url: string) => {
            inviteUrl.value = url;
            generatingInvite.value = false;
          });
        },
        copyToClipboard() {
          if (inviteUrl.value) {
            navigator.clipboard.writeText(inviteUrl.value);
            copiedSnackbar.value = true;
          }
        },
        confirmRemove(member: Profile) {
          memberToRemove.value = member;
          removeDialog.value = true;
        },
        removeMember() {
          if (memberToRemove.value) {
            emit('remove-member', memberToRemove.value.uid);
            removeDialog.value = false;
            memberToRemove.value = undefined;
          }
        },
      };
    },
  });
</script>
